import type { MedplumClient } from '@medplum/core';
import type {
  Bundle,
  Patient,
  Practitioner,
  Provenance,
  Task,
} from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_EXTRACTION_JOB_SYSTEM,
  type ClinicalTaskCategory,
  type ClinicalTaskPriority,
  asClinicalTaskStatus,
  asPriority,
  buildClinicalTaskCode,
  clinicalTaskFindingExtrasSchema,
  clinicalTaskIntentFor,
  clinicalTaskReviewTagCodeFor,
  isClinicalTask,
  readTaskTitle,
} from '@eluve/clinical-tasks';
import { useMedplum } from '@eluve/medplum-client';

import { useMiniAppApi } from '../../hooks';

import {
  CLINICAL_TASKS_AGENT_ID,
  CLINICAL_TASK_CATEGORY_SYSTEM,
  DATA_OPERATION_SYSTEM,
  DERIVED_FROM_APPOINTMENT_SYSTEM,
  PROVENANCE_PARTICIPANT_TYPE_SYSTEM,
  REVIEW_STATUS_SYSTEM,
  SUPPORTING_PASSAGE_EXTENSION,
} from './constants';
import {
  type ClinicalTasksExtraction,
  getTasksPanelSnapshot,
  publishTasksPanel,
} from './tasks-panel-store';

/** A draft task awaiting review — served by the mini-app findings endpoint. */
export interface PendingClinicalTask {
  readonly id: string;
  readonly description: string;
  readonly category: string | null;
  readonly priority: string | null;
  /** FHIR status — always `draft` for a pending task; drives the edit-sheet
   *  Status select and the card badge alongside the accepted set. */
  readonly status: string;
  readonly dueDate: string | null;
  readonly evidence: string | null;
  readonly confidence: number | null;
  readonly ownerReference: string | null;
  /** Patient display name, resolved server-side off Task.for. */
  readonly forDisplay: string | null;
}

/** An accepted task in its Todo/Done lifecycle — read from Medplum directly.
 *  Carries the same display fields as a pending task so the panel renders
 *  both through one card (accepted stays in place, ENG-5243 review note). */
export interface AcceptedClinicalTask {
  readonly id: string;
  readonly description: string;
  readonly category: string | null;
  readonly priority: string | null;
  /** FHIR status (`requested` / `in-progress` / `completed`). */
  readonly status: string;
  readonly dueDate: string | null;
  readonly evidence: string | null;
  readonly ownerReference: string | null;
  readonly forDisplay: string | null;
  readonly done: boolean;
  /** The full resource — the Done flip PUTs it back with the new status. */
  readonly raw: Task;
}

export interface PractitionerOption {
  readonly reference: string;
  readonly display: string;
}

export interface ClinicalTaskEdit {
  readonly description: string;
  readonly category: string;
  readonly priority: string | null;
  /** Lifecycle status the clinician set (any of CLINICAL_TASK_STATUSES). */
  readonly status: string;
  readonly dueDate: string | null;
  readonly ownerReference: string | null;
}

/** The clinician-editable fields sent to the task create/update routes —
 *  the backend twin is `ClinicalTaskWriteInput` in `task-shape.ts`. */
interface ClinicalTaskWriteInput {
  description: string;
  category: ClinicalTaskCategory;
  priority: ClinicalTaskPriority;
  dueDate: string | null;
  ownerReference: string | null;
}

/** Narrow UI-shaped (string) fields to the write contract's enums. The sheet
 *  only produces vocabulary values, so the fallbacks are unreachable in
 *  practice — they keep the narrowing total instead of trusting a cast.
 *  Exported so the tenant Tasks table shapes create/update bodies identically. */
export const toWriteInput = (
  edit: ClinicalTaskEdit,
): ClinicalTaskWriteInput => ({
  description: edit.description,
  category: CLINICAL_TASK_CATEGORIES.includes(
    edit.category as ClinicalTaskCategory,
  )
    ? (edit.category as ClinicalTaskCategory)
    : 'general',
  priority: asPriority(edit.priority) ?? 'routine',
  dueDate: edit.dueDate,
  ownerReference: edit.ownerReference,
});

// MedplumClient caches searches in-memory (60s in the browser); every panel
// read must see writes that landed seconds ago, so read through the cache and
// invalidate Task searches after every mutation.
const NO_CACHE = { cache: 'no-cache' } as const;

// Extraction lands asynchronously after the visit completes, so the panel
// polls (bounded) until the extraction-job marker turns terminal — or, for
// marker-less visits (pre-marker data, Medplum client not ready), until the
// first task appears, mirroring the findings button's bounded poll. A live
// `generating` marker keeps the loop alive even with data showing, so a
// re-generation's superseded drafts refresh without a reload.
export const PANEL_POLL_INTERVAL_MS = 10_000;
export const PANEL_POLL_CAP_MS = 3 * 60_000;

/** Apply the editable field set onto a raw Task, browser-direct (accepted
 *  cards). Field semantics mirror the backend update route: null dueDate
 *  clears the restriction; null owner is left untouched (never cleared). The
 *  chosen status is applied verbatim, with intent + the review-status tag
 *  folded to stay consistent (so the pending/accepted queries keep matching);
 *  requester/identifiers ride through via the spread. Exported so the tenant
 *  Tasks table writes accepted-task edits identically. */
export const applyEditToTask = (raw: Task, edit: ClinicalTaskEdit): Task => {
  const category = CLINICAL_TASK_CATEGORIES.includes(
    edit.category as ClinicalTaskCategory,
  )
    ? (edit.category as ClinicalTaskCategory)
    : 'general';
  const priority = asPriority(edit.priority) ?? 'routine';
  const next: Task = {
    ...raw,
    // Title rides in code.text (the FHIR task title); category label in
    // coding.display. Clear any stale description (pre-title tasks kept the
    // title there); description stays free for a future user-managed field.
    code: buildClinicalTaskCode(category, edit.description),
    priority,
  };
  delete next.description;
  // null dueDate clears the restriction; a date (re)sets it.
  if (edit.dueDate) {
    next.restriction = { period: { end: edit.dueDate } };
  } else {
    delete next.restriction;
  }
  // null owner is left untouched (never cleared); a reference reassigns.
  if (edit.ownerReference) {
    next.owner = { reference: edit.ownerReference };
  }
  // The status the clinician chose (verbatim) → matching intent + review tag.
  const status = asClinicalTaskStatus(edit.status);
  if (status) {
    next.status = status;
    next.intent = clinicalTaskIntentFor(status);
    const others = (next.meta?.tag ?? []).filter(
      (t) => t.system !== REVIEW_STATUS_SYSTEM,
    );
    next.meta = {
      ...next.meta,
      tag: [
        ...others,
        {
          system: REVIEW_STATUS_SYSTEM,
          code: clinicalTaskReviewTagCodeFor(status),
        },
      ],
    };
  }
  return next;
};

/** Attestation Provenance for a browser-direct lifecycle change — the same
 *  shape the backend's reject/attest path writes (`buildAttestationProvenance`
 *  in review.ts): who (the acting clinician, `verifier`) revised which Task,
 *  and when. */
const buildAttestationProvenance = (
  targetReference: string,
  practitionerReference: string,
): Provenance => ({
  resourceType: 'Provenance',
  target: [{ reference: targetReference }],
  recorded: new Date().toISOString(),
  activity: {
    coding: [
      { system: DATA_OPERATION_SYSTEM, code: 'UPDATE', display: 'revise' },
    ],
  },
  agent: [
    {
      type: {
        coding: [
          { system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'verifier' },
        ],
      },
      who: { reference: practitionerReference },
    },
  ],
});

/**
 * Commit a browser-direct Task write. A lifecycle DECISION (status changed:
 * complete/reopen, cancel, or a status edit) is written as a transaction that
 * bundles an attestation Provenance with the PUT — so an accepted task carries
 * the same FHIR-level "who decided this, when" trail as the server-side
 * review path, relevant under post-encounter chart audit. A pure field edit
 * (status unchanged) stays a plain update, matching the backend, which only
 * attests on a status change. Falls back to a plain update when the acting
 * clinician isn't resolvable (no reference to attribute the decision to).
 * Exported so the tenant Tasks table shares this one attestation path.
 */
export async function commitTaskWrite(
  client: MedplumClient,
  next: Task,
  opts: { attest: boolean; practitionerReference: string | null },
): Promise<void> {
  if (opts.attest && next.id && opts.practitionerReference) {
    const url = `Task/${next.id}`;
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        { request: { method: 'PUT', url }, resource: next },
        {
          request: { method: 'POST', url: 'Provenance' },
          resource: buildAttestationProvenance(url, opts.practitionerReference),
        },
      ],
    };
    await client.executeBatch(bundle);
    return;
  }
  await client.updateResource(next);
}

const practitionerDisplay = (p: Practitioner): string => {
  const name = p.name?.[0];
  const joined = [name?.given?.join(' '), name?.family]
    .filter(Boolean)
    .join(' ');
  return joined || name?.text || `Practitioner/${p.id}`;
};

/** One extraction-marker read: the lifecycle status plus the marker's
 *  version stamp — the stamp is how a WAITING regenerate detects that a fast
 *  run started AND finished between two poll ticks (status alone can't tell
 *  the new run's terminal write from the previous run's). */
interface ExtractionRead {
  status: ClinicalTasksExtraction;
  /** marker.meta.lastUpdated, advanced by every run-status write; null when
   *  there is no marker or the read degraded. */
  markerUpdatedAt: string | null;
}

/** Lifecycle of the extraction run off the job marker Task (the
 *  clinical-task-extraction-job identifier — care-plan-generation-job
 *  pattern). No marker / no client / read error all degrade to `unknown`,
 *  which renders exactly like the pre-marker behavior. */
async function fetchExtraction(
  client: MedplumClient | null,
  appointmentId: string,
): Promise<ExtractionRead> {
  if (!client) return { status: 'unknown', markerUpdatedAt: null };
  try {
    const marker = await client.searchOne(
      'Task',
      { identifier: `${CLINICAL_TASK_EXTRACTION_JOB_SYSTEM}|${appointmentId}` },
      NO_CACHE,
    );
    if (!marker) return { status: 'unknown', markerUpdatedAt: null };
    const markerUpdatedAt = marker.meta?.lastUpdated ?? null;
    if (marker.status === 'completed') {
      return { status: 'done', markerUpdatedAt };
    }
    if (marker.status === 'failed' || marker.status === 'cancelled') {
      return { status: 'failed', markerUpdatedAt };
    }
    return { status: 'generating', markerUpdatedAt };
  } catch {
    return { status: 'unknown', markerUpdatedAt: null };
  }
}

/** The patient's display name off the tasks' shared `for` reference, or null.
 *  Task.for is stored as a bare reference (no `display`), so — like the
 *  findings endpoint's server-side `resolveForDisplay` — read the Patient once
 *  for the whole list. Best-effort: a read gap just drops the name. */
async function resolveAcceptedForDisplay(
  client: MedplumClient,
  tasks: Task[],
): Promise<string | null> {
  const ref = tasks.find((t) => t.for?.reference)?.for;
  if (!ref?.reference) return null;
  try {
    const patient = (await client.readReference(ref)) as Patient;
    const name = patient.name?.[0];
    const joined = [name?.given?.join(' '), name?.family]
      .filter(Boolean)
      .join(' ');
    return joined || name?.text || null;
  } catch {
    return null;
  }
}

async function fetchAccepted(
  client: MedplumClient,
  appointmentId: string,
): Promise<AcceptedClinicalTask[]> {
  const found = await client.searchResources(
    'Task',
    {
      identifier: `${DERIVED_FROM_APPOINTMENT_SYSTEM}|${appointmentId}`,
      // The decided (non-draft, non-cancelled) states — all shown in the list.
      status: 'requested,in-progress,completed',
      _count: 200,
    },
    NO_CACHE,
  );
  const tasks = found
    .filter(isClinicalTask)
    .filter((t): t is Task & { id: string } => Boolean(t.id));
  // All of a visit's tasks share `for`; resolve the patient name once. Matches
  // the pending list's server-resolved name so "For" doesn't vanish when a
  // task crosses from pending to accepted.
  const forDisplay = await resolveAcceptedForDisplay(client, tasks);
  return tasks
    .map((t) => ({
      id: t.id,
      description: readTaskTitle(t) ?? '(no description)',
      category:
        t.code?.coding?.find((c) => c.system === CLINICAL_TASK_CATEGORY_SYSTEM)
          ?.code ?? null,
      priority: t.priority ?? null,
      status: t.status ?? 'requested',
      dueDate: t.restriction?.period?.end ?? null,
      // Extension first, note[0] for pre-extension tasks — mirrors the
      // backend's readTaskEvidence.
      evidence:
        t.extension?.find((e) => e.url === SUPPORTING_PASSAGE_EXTENSION)
          ?.valueString ??
        t.note?.[0]?.text ??
        null,
      ownerReference: t.owner?.reference ?? null,
      forDisplay: t.for?.display ?? forDisplay,
      done: t.status === 'completed',
      raw: t,
    }))
    .sort((a, b) => a.description.localeCompare(b.description));
}

/**
 * All panel state + actions for the Clinical Tasks mini-app (ENG-5223).
 *
 * Reads split by trust: the pending (draft) list goes through the mini-app
 * findings endpoint (reviewer identity established server-side); the accepted
 * Todo/Done list and the Done flip go browser-direct under the clinician's own
 * Medplum token — matching the care-plan Accept pattern, so the audit trail
 * shows the clinician, not a service account.
 */
export function useClinicalTasksPanelState(args: {
  tenantId: string;
  appointmentId: string | undefined;
}) {
  const { tenantId, appointmentId } = args;
  const api = useMiniAppApi(tenantId, CLINICAL_TASKS_AGENT_ID);
  // Task-domain routes (RESTful create/update) — app-specific, so they ride
  // the raw ts-rest client rather than the generic mini-apps surface.
  const apiClient = useApiClient();
  const { client, practitionerId } = useMedplum();
  // The acting clinician, for the attestation Provenance a lifecycle change
  // records. Null until the Medplum session resolves — a write then still
  // succeeds, just without an attributable Provenance (best-effort audit).
  const practitionerReference = practitionerId
    ? `Practitioner/${practitionerId}`
    : null;

  const [pending, setPending] = useState<PendingClinicalTask[]>([]);
  const [accepted, setAccepted] = useState<AcceptedClinicalTask[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  // Bumped by `regenerate` to restart the poll loop after it settled.
  const [pollEpoch, setPollEpoch] = useState(0);
  // True from a regenerate request until its run settles — the panel keeps
  // its section visible with an "Extracting tasks…" message instead of
  // vanishing when the cancelled drafts empty the list (the user asked for
  // this run; a disappearing rail reads as a failure).
  const [regenerating, setRegenerating] = useState(false);
  // True once the poll loop has stopped (terminal marker, data showing, or
  // the cap). Until then an EMPTY panel must not render — on a fresh visit
  // the extraction marker reads `unknown` before the job starts, and a
  // premature "No tasks yet." reads as a wrong answer (same rationale as
  // hiding during `generating`).
  const [settled, setSettled] = useState(false);
  // True between a regenerate request and the first tick that sees the new
  // run: the marker still reads the PREVIOUS run's terminal state until the
  // graphile job starts, and settling on that stale value would freeze the
  // panel before the new suggestions exist. "Sees the new run" = in-progress
  // status, OR a marker version stamp different from the pre-request snapshot
  // (a fast run can start and finish entirely between two poll ticks).
  const awaitingRunRef = useRef(false);
  // Marker version stamp from the latest successful read / at request time.
  const markerUpdatedAtRef = useRef<string | null>(null);
  const preRegenerateMarkerRef = useRef<string | null>(null);

  const loadPending = useCallback(async () => {
    if (!appointmentId) return [] as PendingClinicalTask[];
    const res = await api.listFindings(appointmentId);
    if (res.status !== 200) return [] as PendingClinicalTask[];
    return res.body.findings.map((f) => {
      // The task-specific slice rides opaquely in `extras`; tolerate a missing
      // or malformed payload (degrade to nulls) rather than dropping the task.
      const parsed = clinicalTaskFindingExtrasSchema.safeParse(f.extras);
      const extras = parsed.success ? parsed.data : null;
      return {
        id: f.id,
        description: f.display,
        category: extras?.category ?? null,
        priority: extras?.priority ?? null,
        // The findings endpoint only ever serves pending drafts.
        status: 'draft',
        dueDate: extras?.dueDate ?? null,
        evidence: f.evidence,
        confidence: f.confidence,
        ownerReference: extras?.ownerReference ?? null,
        forDisplay: extras?.forDisplay ?? null,
      };
    });
  }, [api, appointmentId]);

  // Which appointment the lists currently reflect. Lets the poll effect skip
  // the `loading` flip when it re-fires for an appointment that is already
  // showing data (e.g. the Medplum client arriving late re-creates `refresh`)
  // — flipping to loading would blank the panel and unmount an open editor.
  const loadedForRef = useRef<string | null>(null);
  // True when the last refresh swallowed a fetch failure (auth still
  // settling, transient FHIR error). The poll must NOT stop on a degraded
  // round — a failed first fetch with a terminal marker would otherwise
  // freeze the panel empty until a reload.
  const degradedRef = useRef(false);

  /** Re-read both lists; resolves true once anything is showing. */
  const refresh = useCallback(async (): Promise<boolean> => {
    if (!appointmentId) {
      setPending([]);
      setAccepted([]);
      loadedForRef.current = null;
      setLoading(false);
      return false;
    }
    try {
      // Pending is served by the mini-app API; the accepted list and the
      // extraction-job marker need the browser Medplum client. The loads fail
      // independently so a broken or still-initializing Medplum login never
      // hides pending review work.
      let degraded = false;
      const [nextPending, nextAccepted, extractionRead] = await Promise.all([
        loadPending().catch(() => {
          degraded = true;
          return [] as PendingClinicalTask[];
        }),
        client
          ? fetchAccepted(client, appointmentId).catch(() => {
              degraded = true;
              return [] as AcceptedClinicalTask[];
            })
          : ([] as AcceptedClinicalTask[]),
        fetchExtraction(client, appointmentId),
      ]);
      degradedRef.current = degraded;
      setPending(nextPending);
      setAccepted(nextAccepted);
      // Keep the last SUCCESSFUL marker stamp — a degraded/absent read must
      // not register as "the marker changed".
      if (extractionRead.markerUpdatedAt !== null) {
        markerUpdatedAtRef.current = extractionRead.markerUpdatedAt;
      }
      // Single source for the Tasks action button (spinner/count) + collapse.
      publishTasksPanel(appointmentId, {
        pending: nextPending.length,
        accepted: nextAccepted.length,
        extraction: extractionRead.status,
      });
      return nextPending.length > 0 || nextAccepted.length > 0;
    } finally {
      loadedForRef.current = appointmentId;
      setLoading(false);
    }
  }, [appointmentId, client, loadPending]);

  useEffect(() => {
    let cancelled = false;
    // `tick` runs async, so `stop` is never invoked before the timers below
    // are initialized.
    const tick = async () => {
      const hasAny = await refresh();
      if (cancelled) {
        stop();
        return;
      }
      const { extraction } = getTasksPanelSnapshot();
      // The regenerate-requested run has started (in-progress marker) or
      // already ran to completion between ticks (marker stamp advanced past
      // the pre-request snapshot) — normal settling resumes. Bounded by the
      // poll cap either way, so a run that never writes its marker can't
      // poll forever.
      const markerAdvanced =
        markerUpdatedAtRef.current !== null &&
        markerUpdatedAtRef.current !== preRegenerateMarkerRef.current;
      if (
        awaitingRunRef.current &&
        (extraction === 'generating' || markerAdvanced)
      ) {
        awaitingRunRef.current = false;
      }
      // Never stop on a degraded round — a swallowed fetch failure plus a
      // terminal marker must retry, not freeze the panel empty. Same for a
      // pending regenerate: its marker is still the previous run's.
      const isSettled =
        !degradedRef.current &&
        !awaitingRunRef.current &&
        (extraction === 'done' ||
          extraction === 'failed' ||
          (hasAny && extraction === 'unknown'));
      if (isSettled) {
        setSettled(true);
        setRegenerating(false);
        stop();
      }
    };
    // Only surface `loading` (which hides the panel) when this appointment's
    // data isn't showing yet — a re-poll of live data refreshes in place.
    if (loadedForRef.current !== (appointmentId ?? null)) setLoading(true);
    // Each (re)started loop resolves afresh; an empty panel hides again
    // until this loop settles.
    setSettled(false);
    void tick();
    const interval = setInterval(() => void tick(), PANEL_POLL_INTERVAL_MS);
    const cap = setTimeout(() => {
      clearInterval(interval);
      // A run that never reports back must not pin "Extracting tasks…" or
      // keep an empty panel hidden forever (manual add needs the rail).
      setSettled(true);
      setRegenerating(false);
    }, PANEL_POLL_CAP_MS);
    function stop() {
      clearInterval(interval);
      clearTimeout(cap);
    }
    return () => {
      cancelled = true;
      stop();
    };
  }, [refresh, pollEpoch]);

  // Assignee options for the edit modal — Practitioner is tenant-member
  // readable, so this is a plain browser-direct read. Loaded once per client.
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    void (async () => {
      try {
        const found = await client.searchResources('Practitioner', {
          _count: 200,
        });
        if (cancelled) return;
        setPractitioners(
          found
            .filter((p): p is Practitioner & { id: string } => Boolean(p.id))
            .map((p) => ({
              reference: `Practitioner/${p.id}`,
              display: practitionerDisplay(p),
            }))
            .sort((a, b) => a.display.localeCompare(b.display)),
        );
      } catch {
        if (!cancelled) setPractitioners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const practitionerName = useCallback(
    (reference: string | null): string | null =>
      practitioners.find((p) => p.reference === reference)?.display ?? null,
    [practitioners],
  );

  /** Accept or reject one pending task through the task review route. */
  const decide = useCallback(
    async (id: string, decision: 'approve' | 'reject'): Promise<boolean> => {
      if (!appointmentId) return false;
      setMutating(true);
      try {
        const res = await apiClient.clinicalTasks.reviewTask({
          params: { tenantId, taskId: id },
          body: { appointmentId, decision },
        });
        if (res.status !== 200) return false;
        client?.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [apiClient, tenantId, appointmentId, client, refresh],
  );

  /** Apply an edit (description/category/priority/due date/assignee); stays
   *  pending. Optimistic: the pending card reflects the edit immediately so
   *  the preview tracks the edit sheet as the user types; a failed write
   *  re-syncs from the server. Deliberately does NOT toggle `mutating` —
   *  autosave fires on every settled change and must not flicker-disable the
   *  panel's buttons. */
  const applyEdit = useCallback(
    async (id: string, edit: ClinicalTaskEdit): Promise<boolean> => {
      if (!appointmentId) return false;
      setPending((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                description: edit.description,
                category: edit.category,
                priority: edit.priority,
                status: edit.status,
                dueDate: edit.dueDate,
                ownerReference: edit.ownerReference,
              }
            : t,
        ),
      );
      try {
        const res = await apiClient.clinicalTasks.updateTask({
          params: { tenantId, taskId: id },
          body: {
            appointmentId,
            ...toWriteInput(edit),
            // The status the clinician picked; the route folds it into intent
            // + review tag and attests when it's a decided value. Unknown/
            // absent falls back to draft (keeps the task pending).
            status: asClinicalTaskStatus(edit.status) ?? 'draft',
          },
        });
        if (res.status !== 200) {
          await refresh();
          return false;
        }
        client?.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        await refresh();
        return false;
      }
    },
    [apiClient, tenantId, appointmentId, client, refresh],
  );

  /** Regenerate this session's suggested tasks (alpha): the route tombstones
   *  the still-pending drafts to `cancelled` and re-enqueues the extraction —
   *  which picks up the clinician's saved custom instructions. Decided tasks
   *  are untouched. Restarts the poll loop so the new run's drafts appear. */
  const regenerate = useCallback(async (): Promise<boolean> => {
    if (!appointmentId) return false;
    setMutating(true);
    // Snapshot the marker BEFORE the request goes out — a fast run can write
    // its terminal marker (and a concurrent tick can observe it) while the
    // POST is still in flight, and a post-response snapshot would then record
    // the NEW run's stamp as "pre-request", making the await unclearable.
    const preRequestMarker = markerUpdatedAtRef.current;
    try {
      const res = await apiClient.clinicalTasks.regenerateTasks({
        params: { tenantId },
        body: { appointmentId },
      });
      if (res.status !== 200) return false;
      client?.invalidateSearches('Task');
      // The await clears when the stamp moves past this snapshot, even if no
      // tick catches the in-progress status.
      preRegenerateMarkerRef.current = preRequestMarker;
      awaitingRunRef.current = true;
      setRegenerating(true);
      setPollEpoch((e) => e + 1);
      return true;
    } catch {
      return false;
    } finally {
      setMutating(false);
    }
  }, [apiClient, tenantId, appointmentId, client]);

  /** Manually create a task for this session — born accepted (To do). */
  const createTask = useCallback(
    async (input: ClinicalTaskEdit): Promise<boolean> => {
      if (!appointmentId) return false;
      setMutating(true);
      try {
        const res = await apiClient.clinicalTasks.createTask({
          params: { tenantId },
          body: { appointmentId, ...toWriteInput(input) },
        });
        if (res.status !== 201) return false;
        client?.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [apiClient, tenantId, appointmentId, client, refresh],
  );

  /** Remove an accepted task: browser-direct cancel under the clinician's
   *  own identity (same trust model as the Done flip). A tombstone, not a
   *  DELETE — mirrors reject's semantics: cancelled + attested tag, plus an
   *  attestation Provenance so the chart shows who cancelled it and when
   *  (same as the server-side reject path). */
  const removeAccepted = useCallback(
    async (task: AcceptedClinicalTask): Promise<boolean> => {
      if (!client) return false;
      setMutating(true);
      try {
        const next: Task = {
          ...task.raw,
          status: 'cancelled',
          meta: {
            ...task.raw.meta,
            tag: [
              ...(task.raw.meta?.tag ?? []).filter(
                (t) => t.system !== REVIEW_STATUS_SYSTEM,
              ),
              { system: REVIEW_STATUS_SYSTEM, code: 'attested' },
            ],
          },
        };
        await commitTaskWrite(client, next, {
          attest: true,
          practitionerReference,
        });
        client.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [client, practitionerReference, refresh],
  );

  /** Edit an accepted task browser-direct under the clinician's own identity
   *  — the update route only edits pending tasks, so accepted edits ride the
   *  Medplum client like the Done flip / remove. Does NOT toggle `mutating`:
   *  the edit sheet autosaves on every settled change (mirrors `applyEdit`). */
  const editAccepted = useCallback(
    async (
      task: AcceptedClinicalTask,
      edit: ClinicalTaskEdit,
    ): Promise<boolean> => {
      if (!client) return false;
      try {
        const next = applyEditToTask(task.raw, edit);
        // A status change is a lifecycle decision → attestation Provenance; a
        // pure field edit is not (matches the backend's non-status edit).
        await commitTaskWrite(client, next, {
          attest: next.status !== task.raw.status,
          practitionerReference,
        });
        client.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      }
    },
    [client, practitionerReference, refresh],
  );

  /** Flip Todo ↔ Done browser-direct under the clinician's own identity. The
   *  status change is a lifecycle decision, so it records an attestation
   *  Provenance (who completed/reopened it, and when). */
  const setDone = useCallback(
    async (task: AcceptedClinicalTask, done: boolean): Promise<boolean> => {
      if (!client) return false;
      setMutating(true);
      try {
        const next: Task = {
          ...task.raw,
          status: done ? 'completed' : 'requested',
        };
        await commitTaskWrite(client, next, {
          attest: true,
          practitionerReference,
        });
        client.invalidateSearches('Task');
        await refresh();
        return true;
      } catch {
        return false;
      } finally {
        setMutating(false);
      }
    },
    [client, practitionerReference, refresh],
  );

  return useMemo(
    () => ({
      pending,
      accepted,
      practitioners,
      practitionerName,
      loading,
      mutating,
      regenerating,
      settled,
      refresh,
      decide,
      applyEdit,
      createTask,
      editAccepted,
      removeAccepted,
      setDone,
      regenerate,
    }),
    [
      pending,
      accepted,
      practitioners,
      practitionerName,
      loading,
      mutating,
      regenerating,
      settled,
      refresh,
      decide,
      applyEdit,
      createTask,
      editAccepted,
      removeAccepted,
      setDone,
      regenerate,
    ],
  );
}
